import React, { useEffect, useRef, useState } from "react";
import Header from "../Header";
import About from "../About";
import Projects from "./Projects";
import Contact from "../Contact";
import Footer from "../Footer";
import { projectsData } from "../data/projectsData";

const heroCodeWall = `import { design, build } from "@lissa/studio"
import { motion } from "framer-motion"

export default function Lisa() {
  const work = ["web", "systems", "ui"]
  const stack = ["React", "Laravel", "API"]

  return (
    <main className="studio min-h-screen">
      <Hello />
      <p>I design websites & build systems</p>
      {work.map((item) => (
        <Tag key={item}>{item}</Tag>
      ))}
    </main>
  )
}

const layout = {
  display: "grid",
  columns: 12,
  gap: "2rem",
  align: "center",
}

.hero {
  min-height: 100vh;
  mix-blend-mode: multiply;
}

@media (min-width: 1024px) {
  .hero { grid-template-columns: 1fr 1fr }
}

.btn-line {
  border-bottom: 1px solid #111;
}

export const site = {
  name: "LISSACODES",
  role: "fullstack",
}

const year = new Date().getFullYear()

function Studio({ children }) {
  return <section id="home">{children}</section>
}

lisa.map((project) => <Work key={project.id} />)

const tokens = {
  ink: "#111111",
  mist: "#8a8a8a",
  blue: "#2563eb",
}

<nav className="flex items-center justify-between">
  <a href="#home">LC</a>
  <BookACall />
</nav>

async function launch(site) {
  await site.build()
  return site.goLive()
}
`;

const heroSnippets = [
  { text: "<Hello />", delay: "0.15s", className: "left-[6%] top-[26%]" },
  { text: "{ design, build }", delay: "0.28s", className: "right-[7%] top-[24%]" },
  { text: "const stack = [\"React\"]", delay: "0.38s", className: "left-[14%] top-[42%]" },
  { text: "export default Lisa", delay: "0.48s", className: "right-[8%] top-[46%]" },
  { text: "grid-cols-12", delay: "0.58s", className: "right-[16%] top-[36%]" },
  { text: "min-h-screen", delay: "0.68s", className: "left-[8%] top-[58%]" },
  { text: "</main>", delay: "0.78s", className: "left-[12%] bottom-[20%]" },
  { text: "npm run build", delay: "0.88s", className: "right-[10%] bottom-[28%]" },
];

const HeroPortrait = () => {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const onMove = (e) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos({
      x: ((e.clientX - r.left) / r.width - 0.5) * 2,
      y: ((e.clientY - r.top) / r.height - 0.5) * 2,
    });
  };

  return (
    <div
      ref={wrapRef}
      className="hero-photo relative flex h-[72vh] items-center justify-center lg:h-screen"
      onMouseMove={onMove}
      onMouseLeave={() => setPos({ x: 0, y: 0 })}
    >
      <div className="hero-editor-bar" aria-hidden="true">
        <span className="hero-dots">
          <i />
          <i />
          <i />
        </span>
        <span>studio/lisa.tsx</span>
        <span className="ml-auto hidden sm:inline">React · Tailwind</span>
      </div>

      <pre className="hero-code-wall" aria-hidden="true">
        {Array(8).fill(heroCodeWall).join("\n")}
      </pre>
      <div className="hero-photo-fade" />

      {heroSnippets.map((s) => (
        <span
          key={s.text}
          className={`hero-chip hidden sm:block ${s.className}`}
          style={{
            animationDelay: s.delay,
            transform: `translate3d(${pos.x * -14}px, ${pos.y * -10}px, 0)`,
          }}
        >
          {s.text}
        </span>
      ))}
      <span
        className="hero-chip bottom-[14%] left-1/2 hidden -translate-x-1/2 sm:flex"
        style={{ transform: `translate3d(calc(-50% + ${pos.x * -8}px), ${pos.y * -6}px, 0)` }}
      >
        const lisa =<span> "studio"</span>
        <i className="hero-cursor" />
      </span>

      <div className="hero-editor-status" aria-hidden="true">
        Ln 12, Col 4 · UTF-8 · Lisa Latifi
      </div>
    </div>
  );
};

const processSteps = [
  {
    step: "01",
    title: "Discover",
    desc: "We define your goals, brand direction, and what the site needs to achieve.",
  },
  {
    step: "02",
    title: "Design + build",
    desc: "The interface — or the full system — is designed and developed as a clean, modern, usable product.",
  },
  {
    step: "03",
    title: "Launch",
    desc: "After refinements, the site goes live — polished, tested, and ready to perform.",
  },
];

const Portfolio = () => {
  const year = new Date().getFullYear();

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth" }));
    }
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-white text-ink">
      <Header />

      <section id="home" className="relative min-h-screen overflow-hidden bg-white">
        <div className="pointer-events-none absolute left-6 top-28 hidden h-[58vh] flex-col items-center justify-between lg:flex">
          <span
            className="text-[11px] uppercase tracking-[0.22em] text-mist"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            Full stack
          </span>
          <span
            className="text-[11px] tracking-[0.22em] text-mist"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            {year}
          </span>
        </div>

        <div className="relative z-10 grid min-h-screen items-center lg:grid-cols-2">
          <div className="px-6 py-12 sm:px-10 lg:pl-24 lg:pr-10 lg:pt-0 xl:pl-32">
            <div className="mb-10 flex gap-14 text-sm">
              <div>
                <p className="text-2xl font-light tracking-tight">+{projectsData.length}</p>
                <p className="mt-1 text-[12px] text-mist">Projects completed</p>
              </div>
              <div>
                <p className="text-2xl font-light tracking-tight">100%</p>
                <p className="mt-1 text-[12px] text-mist">Websites & systems</p>
              </div>
            </div>

            <p className="mb-5 inline-flex items-center gap-2 border border-ink/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-ink/70">
              <i className="h-1.5 w-1.5 rounded-full bg-ink" />
              Available for projects
            </p>

            <h1 className="font-extralight tracking-tight text-ink text-[4.5rem] leading-[0.9] sm:text-[7rem] lg:text-[8.5rem]">
              Hello
            </h1>
            <p className="mt-4 text-sm text-ink/70">— I’m Lisa, fullstack developer. I design websites and build systems.</p>
            <p className="mt-3 font-mono text-[13px] text-[#2563eb]">
              {"<Hello />"}
              <i className="hero-cursor" />
            </p>

            <div className="mt-12 flex flex-wrap items-center gap-6">
              <button type="button" onClick={() => scrollTo("contact")} className="btn-fill">
                Book A Call
              </button>
              <button
                type="button"
                onClick={() => scrollTo("projects")}
                className="text-sm text-ink/70 transition hover:text-ink"
              >
                Selected work ↓
              </button>
            </div>
          </div>

          <HeroPortrait />
        </div>
      </section>

      <div className="site-marquee" aria-hidden="true">
        <div className="site-marquee-track">
          {Array.from({ length: 2 }).map((_, loop) => (
            <p key={loop}>
              Web design — Fullstack — Systems — React — Laravel — APIs — Mobile — Next.js — Tailwind
              — Web design — Fullstack — Systems — React — Laravel — APIs — Mobile — Next.js — Tailwind
            </p>
          ))}
        </div>
      </div>

      <Projects />
      <About />

      <section id="process" className="scroll-mt-24 border-t border-ink/10 py-20 lg:py-24">
        <div className="site-wrap">
          <p className="label">Services</p>
          <h2 className="mt-3 max-w-xl text-3xl font-light tracking-tight sm:text-5xl">
            A clear path from idea to launch.
          </h2>
          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {processSteps.map((s) => (
              <article key={s.step} className="process-card">
                <p className="font-mono text-[11px] text-mist">{s.step}</p>
                <h3 className="mt-8 text-2xl font-light">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink/60">{s.desc}</p>
                <span className="process-ghost">{s.step}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Contact />
      <Footer />
    </div>
  );
};

export default Portfolio;
